import type {
  DeviceGateVerdict,
  ProgressVisibility,
  ResponderStep,
  StepStatus,
} from '@/shared-responder-v2-core';

export const PROGRESS_STATIC_ROW_KINDS = {
  Screener: 'screener',
  Intro: 'intro',
  Outro: 'outro',
} as const;

export type ProgressStaticRowKind =
  (typeof PROGRESS_STATIC_ROW_KINDS)[keyof typeof PROGRESS_STATIC_ROW_KINDS];

/**
 * One row of the progress list, precomputed by the composition root.
 *
 * The affordance and reachability are resolved from the kernel before they reach
 * the shell, so the progress components carry no navigation rules of their own
 * and the list can never disagree with what a jump actually permits.
 */
export interface ResponderProgressItem {
  readonly step: ResponderStep;
  readonly index: number;
  readonly affordance: StepStatus;
  readonly isReachable: boolean;
  /**
   * Set only on the static Intro/Outro/Screener rows the rail prepends and
   * appends around the real questions — absent for a question row. `step.kind`
   * stays a real `ThreadKind` on these rows too (never a fake one), so this is
   * a parallel signal the glyph, the remaining-count and the rail window read
   * instead of overloading `isReachable`, which a branching-barred future
   * question already uses for an unrelated reason.
   */
  readonly staticRowKind?: ProgressStaticRowKind;
}

export interface ResponderProgressProps {
  readonly items: readonly ResponderProgressItem[];
  readonly remainingCount: number;
  readonly visibility: ProgressVisibility;
  readonly onJump: (index: number) => void;
}

export const PROGRESS_ORIENTATIONS = {
  Vertical: 'vertical',
  Horizontal: 'horizontal',
} as const;

export type ProgressOrientation =
  (typeof PROGRESS_ORIENTATIONS)[keyof typeof PROGRESS_ORIENTATIONS];

export interface ResponderDeviceGateProps {
  readonly verdict: DeviceGateVerdict;
  /**
   * The URL to encode as a QR code. Built from the live `window.location.href`
   * rather than reconstructed from the project id, so the locale segment the
   * responder is already on survives the hop to their phone.
   */
  readonly shareUrl: string;
}

export const DICTATION_STATES = {
  Idle: 'idle',
  Listening: 'listening',
  Unavailable: 'unavailable',
} as const;

export type DictationState =
  (typeof DICTATION_STATES)[keyof typeof DICTATION_STATES];

export interface ResponderComposerProps {
  readonly draftText: string;
  /** Live microphone loudness, 0-1. Absent, the waveform breathes at its floor. */
  readonly audioLevel?: number;
  readonly isSendEnabled: boolean;
  /**
   * The responder refused or lost the microphone. The composer keeps working —
   * typing is always a valid way to answer — but it says so, because a voice
   * question with a dead mic and no explanation reads as a broken survey.
   */
  readonly isMicrophoneBlocked?: boolean;
  readonly placeholder?: string;
  readonly dictationState: DictationState;
  readonly isDictating: boolean;
  readonly onTextChange: (text: string) => void;
  readonly onSubmit: () => void;
  readonly onContinue?: () => void;
  readonly onToggleDictation: () => void;
}
